import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Fire, HeartStraight, Lightning, MagnifyingGlass, MapPin, Martini, UsersThree } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, fmtErr, mediaUrl, trackAnalytics } from "../../lib/api";
import { usePublicLocation } from "../../hooks/usePublicCity";
import NotificationMenu from "../../components/NotificationMenu";
import SearchOverlay from "../../components/SearchOverlay";

const FILTERS = [
  { id: "all", label: "Open Now" },
  { id: "trending", label: "Trending" },
  { id: "free_entry", label: "Free Entry" },
  { id: "ladies_free", label: "Ladies Free" },
  { id: "happy_hour", label: "Happy Hour" },
  { id: "vip", label: "VIP" },
];

export function PulseBadge({ pulse, label }) {
  const color = pulse >= 85 ? "text-neon-gold border-neon-gold/50" : pulse >= 65 ? "text-neon-yellow border-neon-yellow/50" : "text-neon-violet border-neon-violet/50";
  return (
    <span data-testid="pulse-badge" className={`inline-flex items-center gap-1.5 rounded-full border bg-black/70 px-2.5 py-1 text-[11px] font-bold ${color}`}>
      <Fire size={13} weight="fill" /> {pulse} · {label}
    </span>
  );
}

export function VenueCard({ venue, onFav, favorited }) {
  const navigate = useNavigate();
  const tags = venue.tags || [];
  const music = venue.music || [];
  const image = venue.image || venue.images?.[0] || "https://images.pexels.com/photos/2114365/pexels-photo-2114365.jpeg";
  return (
    <div data-testid={`venue-card-${venue.id}`}
      className="card-neon cursor-pointer overflow-hidden"
      onClick={() => navigate(`/venue/${venue.id}`)}>
      <div className="relative h-40">
        <img src={mediaUrl(image)} alt={venue.name} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
        <div className="absolute left-3 right-16 top-3 flex flex-wrap items-center gap-1.5">
          <PulseBadge pulse={venue.pulse} label={venue.pulse_label} />
          {venue.founder && (
            <span data-testid={`founder-chip-${venue.id}`}
              className="inline-flex items-center gap-1 rounded-full border border-neon-gold/60 bg-neon-gold/15 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-neon-gold">
              <Crown size={11} weight="fill" /> Founder
            </span>
          )}
        </div>
        <button data-testid={`fav-venue-${venue.id}`}
          onClick={(e) => { e.stopPropagation(); onFav(venue); }}
          className="absolute right-3 top-3 rounded-full bg-black/60 p-2 backdrop-blur transition-transform duration-150 hover:scale-110">
          <HeartStraight size={18} weight={favorited ? "fill" : "regular"} className={favorited ? "text-neon-gold" : "text-white"} />
        </button>
        <div className="absolute bottom-3 left-3 right-3">
          <div className="font-display text-lg font-black">{venue.name}</div>
          <div className="mt-0.5 flex items-center gap-2 text-xs text-zinc-300">
            <span className="flex items-center gap-1"><MapPin size={12} /> {venue.distance_km ?? "—"} mi</span>
            <span>· Open til {venue.open_until || "late"}</span>
            <span>· {venue.cover > 0 ? `$${venue.cover} cover` : "No cover"}</span>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 px-4 py-3">
        {music.map((m) => (
          <span key={m} className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] font-semibold text-zinc-400">{m}</span>
        ))}
        {venue.cover_note && (
          <span className="rounded-full bg-neon-green/10 px-2 py-0.5 text-[10px] font-bold text-neon-green">{venue.cover_note}</span>
        )}
        {tags.includes("vip") && (
          <span className="flex items-center gap-1 rounded-full bg-neon-yellow/10 px-2 py-0.5 text-[10px] font-bold text-neon-yellow"><Crown size={11} /> VIP</span>
        )}
        {venue.tonight_event?.friends_going > 0 && (
          <span className="flex items-center gap-1 rounded-full bg-neon-violet/10 px-2 py-0.5 text-[10px] font-bold text-neon-violet">
            <UsersThree size={11} /> {venue.tonight_event.friends_going} friends going
          </span>
        )}
        {venue.rewards_program?.length > 0 && (
          <span className="flex items-center gap-1 rounded-full bg-neon-gold/10 px-2 py-0.5 text-[10px] font-bold text-neon-gold">
            <Martini size={11} /> Check-in rewards
          </span>
        )}
      </div>
    </div>
  );
}

export default function Discover() {
  const location = usePublicLocation();
  const [venues, setVenues] = useState([]);
  const [events, setEvents] = useState([]);
  const [drops, setDrops] = useState([]);
  const [favs, setFavs] = useState(new Set());
  const [filter, setFilter] = useState("all");
  const [searchOpen, setSearchOpen] = useState(false);
  const [friendPlans, setFriendPlans] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!location.resolved) return;
    const safeGet = (path, fallback) => API.get(path).then((r) => r.data).catch(() => fallback);
    const params = new URLSearchParams();
    if (location.has_location) {
      params.set("city", location.city);
      if (location.lat !== null && location.lng !== null) {
        params.set("lat", location.lat);
        params.set("lng", location.lng);
        params.set("max_km", "50");
      }
    }
    const venueQuery = params.toString() ? `?${params}` : "";
    Promise.all([
      safeGet(`/venues${venueQuery}`, []), safeGet("/events?tonight=true", []), safeGet("/drops", []),
      safeGet("/favorites", []), safeGet("/friends/plans", []),
    ]).then(([v, e, d, f, p]) => {
      setVenues(Array.isArray(v) ? v : []);
      setEvents(Array.isArray(e) ? e : []);
      setDrops(Array.isArray(d) ? d : []);
      setFavs(new Set((Array.isArray(f) ? f : [])
        .filter((x) => x.type === "venue" && x.item?.id).map((x) => x.item.id)));
      setFriendPlans(Array.isArray(p) ? p : []);
    });
  }, [location.resolved, location.has_location, location.city, location.lat, location.lng]);

  const toggleFav = async (venue) => {
    try {
      const { data } = await API.post("/favorites/toggle", { target_type: "venue", target_id: venue.id });
      setFavs((s) => { const n = new Set(s); data.favorited ? n.add(venue.id) : n.delete(venue.id); return n; });
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const filtered = filter === "all" ? venues
    : filter === "free_entry" ? venues.filter((v) => v.tags.includes("free_entry") || v.cover === 0)
    : venues.filter((v) => v.tags.includes(filter));

  return (
    <div data-testid="discover-page" className="px-5 pt-8">
      <header className="flex items-start justify-between">
        <div>
          <div className="neon-overline" data-testid="discover-city-label">Tonight in {location.city}</div>
          <h1 className="mt-1 font-display text-3xl font-black">What's moving<span className="text-neon-gold">.</span></h1>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="search-open-btn" onClick={() => setSearchOpen(true)}
            className="glass rounded-full p-2.5 text-zinc-300 transition-colors hover:text-neon-gold">
            <MagnifyingGlass size={16} />
          </button>
          <NotificationMenu />
        </div>
      </header>
      {searchOpen && <SearchOverlay onClose={() => setSearchOpen(false)} />}

      <div className="no-scrollbar -mx-5 mt-6 flex gap-2 overflow-x-auto px-5" data-testid="filter-chips">
        {FILTERS.map((f) => (
          <button key={f.id} data-testid={`filter-${f.id}`} onClick={() => setFilter(f.id)}
            className={`whitespace-nowrap rounded-full border px-4 py-2 text-xs font-bold transition-[background-color,border-color,color] duration-200 ${filter === f.id ? "border-neon-gold bg-neon-gold text-black shadow-glow-gold" : "border-white/10 bg-white/5 text-zinc-400 hover:border-white/25"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {drops.length > 0 && (
        <section className="mt-8">
          <div className="mb-3 flex items-center gap-2">
            <Lightning size={16} weight="fill" className="text-neon-green" />
            <h2 className="font-display text-base font-bold">NITE DROPS</h2>
            <span className="rounded-full bg-neon-green/15 px-2 py-0.5 text-[10px] font-bold text-neon-green">LIVE</span>
          </div>
          <div className="space-y-3">
            {drops.map((d) => (
              <div key={d.id} data-testid={`drop-${d.id}`}
                className="relative overflow-hidden rounded-2xl border border-neon-green/40 bg-[#07120b] p-4 shadow-glow-green">
                <div className="neon-overline !text-neon-green">{d.title} — {d.venue_name}</div>
                <p className="mt-1.5 font-display text-sm font-bold">{d.perk}</p>
                <p className="mt-1 text-xs text-zinc-400">First {d.total} members to check in.</p>
                <div className="mt-3 flex items-center justify-between">
                  <div className="h-1.5 w-40 overflow-hidden rounded-full bg-white/10">
                    <div className="h-full rounded-full bg-neon-green transition-[width] duration-500" style={{ width: `${(d.claimed / d.total) * 100}%` }} />
                  </div>
                  <span className="text-xs font-bold text-neon-green" data-testid={`drop-remaining-${d.id}`}>{d.remaining} / {d.total} remaining</span>
                </div>
                <button data-testid={`drop-go-${d.id}`} onClick={() => navigate(`/venue/${d.venue_id}`)} className="mt-3 text-xs font-bold uppercase tracking-widest text-white underline decoration-neon-green underline-offset-4">
                  Get there
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {friendPlans.length > 0 && (
        <section className="mt-8" data-testid="friends-tonight">
          <div className="mb-3 flex items-center gap-2">
            <UsersThree size={16} weight="fill" className="text-neon-violet" />
            <h2 className="font-display text-base font-bold">FRIENDS TONIGHT</h2>
          </div>
          <div className="space-y-3">
            {friendPlans.map((p) => (
              <div key={p.event.id} data-testid={`friend-plan-${p.event.id}`}
                onClick={() => navigate(`/venue/${p.event.venue_id}`)}
                className="card-neon cursor-pointer p-4">
                <div className="text-xs font-bold text-neon-violet">
                  {p.friends_going.map((f) => f.handle).join(", ")} {p.friends_going.length === 1 ? "is" : "are"} going
                </div>
                <div className="mt-1 font-display text-base font-black">{p.event.title}</div>
                <div className="text-xs text-zinc-400">{p.venue_name} · {p.event.start}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {events.length > 0 && (
      <section className="mt-8">
        <h2 className="mb-3 font-display text-base font-bold">TONIGHT</h2>
        <div className="no-scrollbar -mx-5 flex gap-3 overflow-x-auto px-5" data-testid="tonight-events">
          {events.map((e) => (
            <div key={e.id} data-testid={`event-card-${e.id}`} onClick={() => { trackAnalytics("event_click", e.venue_id, e.id); navigate(`/venue/${e.venue_id}`); }}
              className="card-neon w-64 shrink-0 cursor-pointer overflow-hidden">
              <div className="relative h-28">
                <img src={mediaUrl(e.image || "https://images.pexels.com/photos/9534908/pexels-photo-9534908.jpeg")} alt={e.title} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
                <div className="absolute bottom-2 left-3 right-3 font-display text-sm font-bold">{e.title}</div>
              </div>
              <div className="px-3 py-2.5">
                <div className="text-xs text-zinc-400">{e.venue_name} · {e.start}{e.end ? `–${e.end}` : ""}</div>
                <div className="mt-1.5 flex items-center gap-2 text-[11px] font-bold">
                  <span className="flex items-center gap-1 text-neon-gold"><Fire size={12} weight="fill" /> {e.going_count} Going</span>
                  {e.friends_going > 0 && <span className="flex items-center gap-1 text-neon-violet"><UsersThree size={12} /> {e.friends_going} friends</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
      )}

      <section className="mt-8">
        <h2 className="mb-3 font-display text-base font-bold">POPULAR NEAR YOU</h2>
        <div className="space-y-4" data-testid="venue-list">
          {filtered.map((v) => (
            <VenueCard key={v.id} venue={v} onFav={toggleFav} favorited={favs.has(v.id)} />
          ))}
          {filtered.length === 0 && (
            <div className="card-neon p-8 text-center" data-testid="empty-venues">
              <div className="font-display text-lg font-black">The night is being built</div>
              <p className="mt-2 text-sm text-zinc-500">
                {filter === "all"
                  ? "No partner venues are live in your city yet. New spots join every week — check back soon."
                  : "Nothing in this lane tonight. Check another filter."}
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
